/**
 * API client — thin wrappers around fetch for the FastAPI backend.
 * All endpoints are relative (proxied in dev, same-origin in production).
 */

import type {
  WorkflowSummary,
  WorkflowDef,
  WorkflowExecution,
  ExcelUploadResult,
  WorkflowSchedule,
  MemoryRecallItem,
  AutomationSummary,
  Automation,
  AutomationRun,
  AutomationVersion,
} from "@/types";
import { getIdToken } from "@/auth/cognito";

// ── Helpers ─────────────────────────────────────────

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const auth = await authHeaders();
  const res = await fetch(url, {
    ...init,
    headers: { ...auth, ...init?.headers },
  });
  if (!res.ok) {
    // Surface the server's {"detail": "..."} message instead of the
    // generic "400 Bad Request" — gives the user something actionable.
    let detail = "";
    try {
      const body = await res.json();
      detail = body?.detail || body?.message || "";
    } catch {
      try { detail = (await res.text()).slice(0, 300); } catch {}
    }
    throw new Error(detail ? `${res.status} ${detail}` : `${res.status} ${res.statusText}`);
  }
  return res.json();
}

function post<T>(url: string, body: unknown): Promise<T> {
  return json<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── Chat ────────────────────────────────────────────

/** Returns a ReadableStream for SSE consumption. */
export async function chatStream(
  message: string,
  memoryEnabled = false,
  fileRef?: string,
  sessionId?: string,
  userId?: string,
  tenantId?: string,
): Promise<Response> {
  const auth = await authHeaders();
  const body: Record<string, unknown> = {
    message,
    memory_enabled: memoryEnabled,
  };
  if (fileRef) body.file_ref = fileRef;
  if (sessionId) body.session_id = sessionId;
  if (userId) body.user_id = userId;
  if (tenantId) body.tenant_id = tenantId;
  return fetch("/api/chat/stream", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth },
    body: JSON.stringify(body),
  });
}

/** v2.2 task 9 — approve a committed chain_plan. POSTs to the same
 *  /api/chat/stream surface so the response funnels through the
 *  normal SSE consume loop; `text: "approve_chain"` is the sentinel
 *  the planner pre-classifier (task 10) looks for to bypass the LLM
 *  and dispatch the chain with the user's prereq answers threaded in.
 *  Until task 10 lands the classifier treats this as conversational —
 *  expected dev behaviour. */
export async function approveChainStream(
  planId: string,
  prereqAnswers: Record<string, unknown>,
  sessionId?: string,
  userId?: string,
  tenantId?: string,
): Promise<Response> {
  const auth = await authHeaders();
  const body: Record<string, unknown> = {
    message: "approve_chain",
    text: "approve_chain",
    plan_id: planId,
    prereq_answers: prereqAnswers,
    memory_enabled: true,
  };
  if (sessionId) body.session_id = sessionId;
  if (userId) body.user_id = userId;
  if (tenantId) body.tenant_id = tenantId;
  return fetch("/api/chat/stream", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth },
    body: JSON.stringify(body),
  });
}

export function clearChat(): Promise<void> {
  return json("/api/clear", { method: "POST" });
}

// ── 4-service architecture health (arch-change 2026-06-04) ──
// Aggregated snapshot of website / gateway / planner / memory_worker
// for the DevPanel "Services" card. Backed by /api/services/health.

export interface ServiceHealth {
  name: string;                     // website | gateway | planner | memory_worker
  status: "ok" | "degraded" | "down" | "unknown";
  latency_ms?: number | null;
  code?: number;                    // HTTP code when degraded
  error?: string;                   // when down/unknown
  stuck_locks?: number;             // memory_worker only
}

export interface PlannerBreakerSnapshot {
  state: "closed" | "open" | "half_open" | "unknown";
  consecutive_failures: number;
}

export interface ServicesHealth {
  overall: "ok" | "degraded";
  services: ServiceHealth[];
  planner_breaker: PlannerBreakerSnapshot;
  arch_version: string;
}

export function fetchServicesHealth(): Promise<ServicesHealth> {
  return json<ServicesHealth>("/api/services/health");
}

/** Signal end-of-session so memory layer can flush + compact. */
export function endSession(sessionId: string): Promise<{ status: string }> {
  return post("/api/session/end", {
    session_id: sessionId,
  });
}

export function confirmWrite(
  confirmId: string,
  approved: boolean,
): Promise<{ status: string }> {
  return post("/api/chat/confirm", { confirm_id: confirmId, approved });
}

/** Stop button — request the in-flight planner turn to abort at the next
 * step boundary. Backend looks up the session's PlannerAgent in
 * _planner_pool, sets cancel_event; run_turn polls + emits Q4
 * `chain_aborted` on next iteration. session_id is optional: when
 * omitted the backend resolves the active session from the JWT user. */
export function cancelChat(
  sessionId?: string,
): Promise<{ ok: boolean; session_id?: string; reason?: string }> {
  return post("/api/chat/cancel", sessionId ? { session_id: sessionId } : {});
}

// ── Memory inspection ───────────────────────────────

export async function fetchMemories(): Promise<MemoryRecallItem[]> {
  const data = await json<{ memories: MemoryRecallItem[] }>("/api/memories");
  return data.memories;
}

export function clearAllMemory(): Promise<{ cleared: boolean }> {
  return post("/api/memory/clear", {});
}

// ── Customer Knowledge view ─────────────────────────
// Backed by memory-manager's `/api/knowledge`. Same auth cookie as
// `/api/memories`. Pre-filtered + pre-grouped on the server.

export interface KnowledgeLearnedFrom {
  kind: string | null;
  session_id: string | null;
  message_id: string | null;
  event_id: string | null;
}

export interface KnowledgeRow {
  id: string;
  type: string;
  human_readable: string;
  learned_on: string;
  learned_from: KnowledgeLearnedFrom;
  confidence: number | null;
}

export interface CustomerKnowledge {
  tenant_id: string;
  as_of: string;
  sections: {
    people: KnowledgeRow[];
    customers: KnowledgeRow[];
    conventions: KnowledgeRow[];
    preferences: KnowledgeRow[];
  };
  counts: {
    people: number;
    customers: number;
    conventions: number;
    preferences: number;
    total: number;
  };
}

export async function fetchKnowledge(): Promise<CustomerKnowledge> {
  return json<CustomerKnowledge>("/api/knowledge");
}

export interface KnowledgeFeedbackBody {
  action: "confirm" | "delete" | "fix";
  type: string;
  new_text?: string;
}

export interface KnowledgeFeedbackResult {
  status: string;
  new_item_id?: string;
  new_version?: number;
}

export function sendKnowledgeFeedback(
  itemId: string,
  body: KnowledgeFeedbackBody,
): Promise<KnowledgeFeedbackResult> {
  return post(`/api/knowledge/${itemId}/feedback`, body);
}

// ── Memory Chunks ───────────────────────────────────

export interface MemoryChunk {
  id: string;
  content: string;
  channel: string;
  source_type: string;
  source_id: string | null;
  related_entity: string | null;
  metadata: Record<string, unknown>;
  created_at: string | null;
  expires_at: string | null;
}

export interface MemoryGraphNode {
  id: string;
  label: string;
  channel: string;
  source_type: string;
  related_entity: string | null;
  created_at: string | null;
  node_type?: "chunk" | "entity";
  sensitivity?: string;
  sender?: string;
  recipient?: string;
  subject?: string;
  thread_id?: string;
}

export interface MemoryGraphEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  weight: number;
  entity_type?: string;
  entity_id?: string;
}

export interface MemoryGraph {
  nodes: MemoryGraphNode[];
  edges: MemoryGraphEdge[];
}

export interface CrmGraphNode {
  id: string;
  label: string;
  path_count: number;
  depth: number;
}

export interface CrmGraphEdge {
  id: string;
  source: string;
  target: string;
  path_count: number;
  examples: string[];
}

export interface CrmEndpointGraph {
  nodes: CrmGraphNode[];
  edges: CrmGraphEdge[];
  chains: Array<{ path: string; tokens: string[] }>;
  path_count: number;
  entity_count: number;
  transition_count: number;
}

export async function getCrmEndpointGraph(): Promise<CrmEndpointGraph> {
  return json("/api/crm/graph");
}

export async function listMemoryChunks(opts?: {
  channel?: string;
  limit?: number;
  offset?: number;
}): Promise<{ chunks: MemoryChunk[]; total: number }> {
  const params = new URLSearchParams();
  if (opts?.channel) params.set("channel", opts.channel);
  if (opts?.limit) params.set("limit", String(opts.limit));
  if (opts?.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return json(`/api/memory/chunks${qs ? `?${qs}` : ""}`);
}

export async function searchMemoryChunks(
  query: string,
  opts?: {
    channel?: string;
    related_entity?: string;
    top_k?: number;
  },
): Promise<{ results: (MemoryChunk & { similarity: number })[] }> {
  return post("/api/memory/chunks/search", { query, ...opts });
}

export async function getMemoryGraph(): Promise<MemoryGraph> {
  return json("/api/memory/graph");
}

export interface TaskLogEntry {
  operation_id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  entity_label: string | null;
  summary: string;
  status: string;
  created_at: string | null;
}

export async function getOperationsLog(): Promise<{ operations: TaskLogEntry[] }> {
  return json("/api/operations");
}

export async function ingestMemoryChunk(data: {
  content: string;
  channel?: string;
  source_type?: string;
  related_entity?: string;
}): Promise<{ chunk_ids: string[] }> {
  return post("/api/memory/chunks/ingest", data);
}

// ── Workflows (backed by automations) ───────────────

export async function listWorkflows(): Promise<WorkflowSummary[]> {
  const autos = await listAutomations();
  return autos.map((a) => ({
    id: a.id,
    name: a.name,
    description: a.description,
    steps_count: 0,
    input_columns: a.input_columns || [],
    output_columns: a.output_columns || {},
    schedule: a.schedule ?? undefined,
  }));
}

export async function getWorkflow(id: string): Promise<WorkflowDef> {
  const a = await getAutomation(id);
  return {
    id: a.id,
    name: a.name,
    description: a.description,
    input_columns: a.input_columns || [],
    steps: a.recipe || [],
    output_columns: a.output_columns || {},
    created_at: a.created_at,
    schedule: a.schedule ?? undefined,
  };
}

export function saveWorkflow(wf: WorkflowDef): Promise<{ id: string }> {
  return createAutomation({
    name: wf.name,
    description: wf.description,
    type: wf.input_columns.length > 0 ? "workflow" : "report",
    recipe: wf.steps,
    input_columns: wf.input_columns,
    output_columns: wf.output_columns,
    schedule: wf.schedule,
  });
}

export async function deleteWorkflow(id: string): Promise<void> {
  return deleteAutomation(id);
}

export function updateSchedule(
  workflowId: string,
  schedule: Partial<WorkflowSchedule>,
): Promise<{ status: string }> {
  return updateAutomation(workflowId, { schedule });
}

// ── Executions ──────────────────────────────────────

export function listExecutions(): Promise<WorkflowExecution[]> {
  return json("/api/executions");
}

export function getExecution(id: string): Promise<WorkflowExecution> {
  return json(`/api/executions/${id}`);
}

export async function runWorkflowStream(
  workflowId: string,
  inputData: Record<string, string>[],
): Promise<Response> {
  const auth = await authHeaders();
  return fetch("/api/executions/run/stream", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth },
    body: JSON.stringify({ workflow_id: workflowId, input_data: inputData }),
  });
}

// ── Actions (successor system; legacy Automations stays alive) ──
// Backed by api/main.py `/api/actions/*`. Per planner-manager
// 2026-05-26: LIST + GET + RUN are live. PATCH / DELETE / runs-history
// / versions-history endpoints are still TODO server-side — request
// from planner-manager when the UI needs them.

export function listActions(): Promise<import("@/types").ActionSummary[]> {
  return json("/api/actions");
}

export function getAction(id: string): Promise<import("@/types").Action> {
  return json(`/api/actions/${id}`);
}

export function runAction(
  id: string,
  body: {
    extracted?: Record<string, unknown>;
    resolved?: Record<string, unknown>;
    trigger_payload?: Record<string, unknown>;
  },
): Promise<import("@/types").ActionRun> {
  return post(`/api/actions/${id}/run`, body);
}

export async function runActionUpload(
  id: string,
  file: File,
): Promise<import("@/types").ActionRun> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/actions/${id}/run-upload`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(body || `Upload run failed (${res.status})`);
  }
  return (await res.json()) as import("@/types").ActionRun;
}

export async function deleteAction(id: string): Promise<void> {
  const res = await fetch(`/api/actions/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(body || `Delete failed (${res.status})`);
  }
}

// ── Automations ─────────────────────────────────────

export function listAutomations(): Promise<AutomationSummary[]> {
  return json("/api/automations");
}

export function getAutomation(id: string): Promise<Automation> {
  return json(`/api/automations/${id}`);
}

export function createAutomation(data: {
  name: string;
  description?: string;
  type: "report" | "workflow";
  recipe?: unknown[];
  input_columns?: string[];
  output_columns?: Record<string, string>;
  schedule?: Partial<WorkflowSchedule> | null;
  visibility?: "private" | "org";
}): Promise<{ id: string }> {
  return post("/api/automations", data);
}

export function updateAutomation(
  id: string,
  data: Partial<{
    name: string;
    description: string;
    recipe: unknown[];
    input_columns: string[];
    output_columns: Record<string, string>;
    schedule: Partial<WorkflowSchedule> | null;
    visibility: "private" | "org";
    enabled: boolean;
  }>,
): Promise<{ status: string }> {
  return json(`/api/automations/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function deleteAutomation(id: string): Promise<void> {
  const auth = await authHeaders();
  await fetch(`/api/automations/${id}`, { method: "DELETE", headers: auth });
}

export function listAutomationRuns(
  automationId: string,
): Promise<AutomationRun[]> {
  return json(`/api/automations/${automationId}/runs`);
}

export function listAutomationVersions(
  automationId: string,
): Promise<AutomationVersion[]> {
  return json(`/api/automations/${automationId}/versions`);
}

export async function revertAutomation(
  automationId: string,
  versionNumber: number,
): Promise<{ status: string; version: number; message: string }> {
  const auth = await authHeaders();
  const res = await fetch(
    `/api/automations/${automationId}/revert/${versionNumber}`,
    {
      method: "POST",
      headers: auth,
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Revert failed" }));
    throw new Error(err.detail || `Revert failed (${res.status})`);
  }
  return res.json();
}

export async function runAutomation(automationId: string): Promise<{
  status: string;
  run_id?: string;
  total_rows?: number;
  download_url?: string;
  export_filename?: string;
  message?: string;
}> {
  const auth = await authHeaders();
  const res = await fetch(`/api/automations/${automationId}/run`, {
    method: "POST",
    headers: auth,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Run failed" }));
    throw new Error(err.detail || `Run failed (${res.status})`);
  }
  return res.json();
}

// ── File Upload ─────────────────────────────────────

export async function uploadFile(file: File): Promise<ExcelUploadResult> {
  const auth = await authHeaders();
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/upload/file", {
    method: "POST",
    headers: auth,
    body: form,
  });
  const data = await res.json();
  if (!res.ok && !data.status) {
    return {
      status: "error",
      message: data.detail || `Upload failed (${res.status})`,
    };
  }
  return data;
}

export async function uploadExcel(file: File): Promise<ExcelUploadResult> {
  return uploadFile(file);
}

// ── Chat persistence ────────────────────────────────

export interface SavedChatSummary {
  chat_id: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

export interface SavedChat extends SavedChatSummary {
  messages: { role: string; content: string }[];
  memory?: Record<string, unknown>;
}

export function listSavedChats(): Promise<SavedChatSummary[]> {
  return json("/api/chats");
}

export function getSavedChat(chatId: string): Promise<SavedChat> {
  return json(`/api/chats/${chatId}`);
}

export function saveChat(
  chatId: string,
  title: string,
  messages: { role: string; content: string }[],
  memory?: Record<string, unknown>,
): Promise<{ status: string; chat_id: string }> {
  return json(`/api/chats/${chatId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, messages, memory: memory || {} }),
  });
}

export function restoreChat(
  messages: { role: string; content: string }[],
  memory: Record<string, unknown>,
): Promise<{ status: string }> {
  return post("/api/chat/restore", { messages, memory });
}

export function deleteSavedChat(chatId: string): Promise<{ status: string }> {
  return json(`/api/chats/${chatId}`, { method: "DELETE" });
}

// ── User profile & onboarding ───────────────────────

export interface UserProfile {
  needs_onboarding: boolean;
  user_id?: string;
  tenant_id?: string | null;
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
}

export function getMe(): Promise<UserProfile> {
  return json("/api/me");
}

export function createMe(): Promise<{
  status: string;
  user_id: string;
  tenant_id: string | null;
  needs_onboarding: boolean;
}> {
  return json("/api/me", { method: "POST" });
}

export interface OnboardPayload {
  invite_code: string;
  company_name: string;
  crm_type?: string;
  crm_base_url?: string;
  crm_api_key?: string;
  crm_company_id?: string;
  crm_access_level?: "read_only" | "full_access";
  first_name?: string;
  last_name?: string;
}

export function onboard(payload: OnboardPayload): Promise<{
  status: string;
  user_id: string;
  tenant_id: string;
  role: string;
}> {
  return post("/api/onboard", payload);
}

export function testCrmConnection(payload: {
  crm_type: string;
  crm_base_url: string;
  crm_api_key: string;
  crm_company_id?: string;
}): Promise<{
  status: string;
  message: string;
  jobs_found: boolean;
  customers_found: boolean;
}> {
  return post("/api/crm/test", payload);
}

export interface ValidateInviteResult {
  valid: boolean;
  error?: string;
  is_team_invite?: boolean;
  company_name?: string;
  tenant_id?: string;
}

export function validateInvite(code: string): Promise<ValidateInviteResult> {
  return post("/api/validate-invite", { code });
}

// ── Organisation / team ─────────────────────────────

export interface TeamMember {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  department: string | null;
  job_title: string | null;
  whatsapp_enabled: boolean;
  role: string;
  created_at: string;
}

export interface TeamResponse {
  company_name: string | null;
  members: TeamMember[];
}

export function fetchTeam(): Promise<TeamResponse> {
  return json("/api/team");
}

export interface InviteCode {
  code: string;
  max_uses: number;
  uses_so_far: number;
  expires_at: string | null;
  created_at: string;
  permission_level?: string;
}

export function fetchInviteCodes(): Promise<InviteCode[]> {
  return json("/api/invite-codes");
}

export function generateInviteCode(
  maxUses = 5,
  expiresInDays?: number,
  permissionLevel?: string,
): Promise<{ code: string; max_uses: number; expires_at: string | null }> {
  return post("/api/invite-codes", {
    max_uses: maxUses,
    expires_in_days: expiresInDays,
    permission_level: permissionLevel,
  });
}

// ── Email Integration (Gateway) ─────────────────────

const GATEWAY_KEY =
  import.meta.env.VITE_GATEWAY_API_KEY || "foreman-internal-gateway-key";

function gatewayHeaders(): Record<string, string> {
  return { "X-Foreman-Key": GATEWAY_KEY };
}

async function gatewayJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { ...gatewayHeaders(), ...init?.headers },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

function gatewayPost<T>(url: string, body?: unknown): Promise<T> {
  return gatewayJson<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
}

export interface EmailConnectionStatus {
  connected: boolean;
  email_address?: string;
  display_name?: string;
}

export function getEmailStatus(
  userId: string,
  tenantId: string,
): Promise<EmailConnectionStatus> {
  return gatewayJson(
    `/gateway/email/status/${encodeURIComponent(userId)}?tenant_id=${encodeURIComponent(tenantId)}`,
  );
}

export function emailAuthStart(
  userId: string,
  tenantId: string,
): Promise<{ auth_url: string }> {
  return gatewayJson(
    `/gateway/email/auth/start?user_id=${encodeURIComponent(userId)}&tenant_id=${encodeURIComponent(tenantId)}`,
  );
}

export function emailDisconnect(
  userId: string,
  tenantId: string,
): Promise<{ status: string }> {
  return gatewayJson(
    `/gateway/email/disconnect/${encodeURIComponent(userId)}?tenant_id=${encodeURIComponent(tenantId)}`,
    { method: "DELETE" },
  );
}

export interface EmailPreferences {
  user_id: string;
  tenant_id: string;
  inbox_monitoring: boolean;
  processing_mode: string;
  digest_schedule: string;
  use_classifier: boolean;
  daily_agent_cap: number;
  smart_triage: boolean;
}

export function getEmailPreferences(
  userId: string,
  tenantId: string,
): Promise<EmailPreferences> {
  return gatewayJson(
    `/gateway/email/preferences/${encodeURIComponent(userId)}?tenant_id=${encodeURIComponent(tenantId)}`,
  );
}

export function updateEmailPreferences(
  userId: string,
  tenantId: string,
  data: Partial<Omit<EmailPreferences, "user_id" | "tenant_id">>,
): Promise<{ status: string }> {
  return gatewayJson(
    `/gateway/email/preferences/${encodeURIComponent(userId)}?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  );
}

export interface EmailRuleItem {
  id: string;
  rule_type: string;
  rule_value: string | null;
  action: string;
  priority: number;
  enabled: boolean;
}

export function listEmailRules(
  userId: string,
  tenantId: string,
): Promise<EmailRuleItem[]> {
  return gatewayJson(
    `/gateway/email/rules/${encodeURIComponent(userId)}?tenant_id=${encodeURIComponent(tenantId)}`,
  );
}

export function createEmailRule(
  userId: string,
  tenantId: string,
  rule: {
    rule_type: string;
    rule_value?: string;
    action?: string;
    priority?: number;
  },
): Promise<{ status: string }> {
  return gatewayPost(
    `/gateway/email/rules/${encodeURIComponent(userId)}?tenant_id=${encodeURIComponent(tenantId)}`,
    rule,
  );
}

export function deleteEmailRule(ruleId: string): Promise<{ status: string }> {
  return gatewayJson(`/gateway/email/rules/${encodeURIComponent(ruleId)}`, {
    method: "DELETE",
  });
}

export interface EmailDraftItem {
  id: string;
  original_from: string | null;
  original_subject: string | null;
  draft_body: string;
  status: string;
  created_at: string | null;
}

export function listEmailDrafts(
  userId: string,
  tenantId: string,
  status = "pending",
): Promise<EmailDraftItem[]> {
  return gatewayJson(
    `/gateway/email/drafts/${encodeURIComponent(userId)}?tenant_id=${encodeURIComponent(tenantId)}&status=${encodeURIComponent(status)}`,
  );
}

export function sendEmailDraft(
  draftId: string,
): Promise<{ id: string; status: string }> {
  return gatewayPost(
    `/gateway/email/drafts/${encodeURIComponent(draftId)}/send`,
  );
}

export function discardEmailDraft(
  draftId: string,
): Promise<{ id: string; status: string }> {
  return gatewayPost(
    `/gateway/email/drafts/${encodeURIComponent(draftId)}/discard`,
  );
}

export function noteEmailDraft(
  draftId: string,
): Promise<{ id: string; status: string }> {
  return gatewayPost(
    `/gateway/email/drafts/${encodeURIComponent(draftId)}/note`,
  );
}

export function emailReply(
  messageId: string,
  userId: string,
  tenantId: string,
  body: string,
): Promise<{ status: string }> {
  return gatewayPost(`/gateway/email/reply/${encodeURIComponent(messageId)}`, {
    user_id: userId,
    tenant_id: tenantId,
    body,
  });
}

// ── Organisation Graph ──────────────────────────────

export interface CanonicalField {
  value: unknown;
  confidence: number;
  source: string;
  last_updated: string;
  extraction_method: string;
}

export type CanonicalSection = Record<string, CanonicalField>;
export type CanonicalSchema = Record<string, CanonicalSection>;

export interface SectionCompleteness {
  total: number;
  populated: number;
  completeness_pct: number;
}

export interface SchemaCompleteness {
  total_fields: number;
  populated: number;
  empty: number;
  completeness_pct: number;
  sections: Record<string, SectionCompleteness>;
}

export interface OrgProfile {
  id: string;
  name: string | null;
  primary_domain: string;
  website: string | null;
  industry: string | null;
  relationship_type: string;
  description: string | null;
  confidence: number;
  is_tenant_root: boolean;
  simpro_customer_id: string | null;
  simpro_supplier_id: string | null;
  domains: string[];
  artifact_count: number;
  created_at: string | null;
}

export interface PersonProfile {
  id: string;
  organisation_id: string | null;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  phone: string | null;
  role: string | null;
  profile_type: string;
  profile_summary: string | null;
  confidence: number;
  account_user_id: string | null;
  interaction_count: number;
  artifact_count: number;
  created_at: string | null;
}

export interface OrgGraphEdge {
  source: string;
  target: string;
  weight: number;
  type: string;
}

export interface OrgGraphData {
  organisations: OrgProfile[];
  people: PersonProfile[];
  edges: OrgGraphEdge[];
}

export function getOrgGraph(tenantId: string): Promise<OrgGraphData> {
  return gatewayJson(
    `/gateway/org/graph?tenant_id=${encodeURIComponent(tenantId)}`,
  );
}

export interface PersonDetail extends PersonProfile {
  aliases: { alias: string; type: string; confidence: number }[];
  recent_emails: {
    id: string;
    subject: string | null;
    sender: string | null;
    created_at: string | null;
  }[];
  canonical_schema: CanonicalSchema | null;
  schema_completeness: SchemaCompleteness | null;
  crm_links?: CrmLink[];
}

export interface CrmLink {
  crm_type: string;
  entity_type: string;
  crm_id: string;
  crm_name?: string;
  parent_id?: string;
  parent_name?: string;
  is_primary?: boolean;
  confidence: number;
  match_reason: string;
}

export function getPersonDetail(personId: string): Promise<PersonDetail> {
  return gatewayJson(`/gateway/org/person/${encodeURIComponent(personId)}`);
}

export interface OrgDetail extends OrgProfile {
  people: {
    id: string;
    full_name: string | null;
    primary_email: string | null;
    role: string | null;
    profile_type: string;
  }[];
  artifact_count: number;
  canonical_schema: CanonicalSchema | null;
  schema_completeness: SchemaCompleteness | null;
  crm_links?: CrmLink[];
}

export function getOrgDetail(orgId: string): Promise<OrgDetail> {
  return gatewayJson(`/gateway/org/organisation/${encodeURIComponent(orgId)}`);
}

export function triggerOrgBootstrap(
  userId: string,
  tenantId: string,
  days = 14,
): Promise<{ status: string }> {
  return gatewayPost(
    `/gateway/org/bootstrap/${encodeURIComponent(userId)}?tenant_id=${encodeURIComponent(tenantId)}&days=${days}`,
  );
}

export function triggerOrgEnrich(
  orgId: string,
  tenantId: string,
): Promise<{ status: string; domain?: string }> {
  return gatewayPost(
    `/gateway/org/enrich/${encodeURIComponent(orgId)}?tenant_id=${encodeURIComponent(tenantId)}`,
  );
}

export function triggerEnrichmentSweep(
  tenantId: string,
): Promise<{ status: string }> {
  return gatewayPost(
    `/gateway/org/enrich-sweep?tenant_id=${encodeURIComponent(tenantId)}`,
  );
}

// ── Stubs for WIP features (MissionBar, WhatsApp, ReportIssue) ──

export interface Mission {
  key: string;
  completed: boolean;
  completed_at: string | null;
}

export function getMissions(): Promise<{ missions: Mission[] }> {
  return json("/api/onboarding/missions");
}

export function completeMission(missionKey: string): Promise<void> {
  return post("/api/onboarding/missions/complete", { mission_key: missionKey });
}

export function dismissOnboarding(): Promise<void> {
  return post("/api/onboarding/dismiss", {});
}

// ── Discovery interview ─────────────────────────────

export function startDiscovery(
  mode: "text" | "call",
): Promise<{ status: string; mode: string }> {
  return post("/api/discovery/start", { mode });
}

export function skipDiscovery(): Promise<{ status: string }> {
  return post("/api/discovery/skip", {});
}

export function completeDiscovery(): Promise<{ status: string }> {
  return post("/api/discovery/complete", {});
}

export function reportIssue(data: {
  description: string;
  category?: string;
}): Promise<{ id: string }> {
  return post("/api/report-issue", data);
}

export function getWhatsAppStatus(
  userId = "default_user",
  tenantId = "default",
): Promise<{
  phone: string | null;
  verified: boolean;
}> {
  return json(`/gateway/whatsapp/status/${userId}?tenant_id=${tenantId}`);
}

export function requestWhatsAppOtp(
  phone: string,
  userId = "default_user",
  tenantId = "default",
): Promise<void> {
  return post("/gateway/whatsapp/request-otp", {
    phone,
    user_id: userId,
    tenant_id: tenantId,
  });
}

export function verifyWhatsAppOtp(
  phone: string,
  otp: string,
  userId = "default_user",
  tenantId = "default",
): Promise<void> {
  return post("/gateway/whatsapp/verify-otp", {
    phone,
    otp,
    user_id: userId,
    tenant_id: tenantId,
  });
}

export function removeWhatsAppPhone(): Promise<void> {
  return post("/gateway/whatsapp/remove", {});
}

// ── Teams pairing ────────────────────────────────
// Production-grade Teams onboarding: user gets a 6-digit code from
// the web app and sends it as `!pair 384721` in the Teams DM with
// the Foreman bot. No Graph admin consent required.

export interface TeamsPairingState {
  active: boolean;          // a code is generated and not yet redeemed
  linked: boolean;          // the user has a verified teams_aad_oid
  code?: string;            // present when active=true
  expires_at?: string;      // ISO timestamp
  redeemed_at?: string;     // ISO timestamp; present after pairing succeeded
  expired?: boolean;
  ttl_seconds?: number;
}

export function startTeamsPairing(): Promise<TeamsPairingState> {
  return json("/api/teams/pairing/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
}

export function getTeamsPairingStatus(): Promise<TeamsPairingState> {
  return json("/api/teams/pairing/status");
}

export function revokeTeamsPairing(): Promise<{ revoked: number }> {
  return json("/api/teams/pairing/revoke", { method: "DELETE" });
}
